import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineBookOpen, HiOutlineUpload } from 'react-icons/hi';
import { API_BASE_URL, fetchReadings } from '../services/api';

// utils/formatNumber.js

/**
 * Abbreviate a number to K (thousands), M (millions), B (billions), etc.
 * @param {number} num - The number to abbreviate
 * @param {number} [decimals=1] - Number of decimal places
 * @return {string} The abbreviated string (e.g. 1.5K, 1.2M)
 */
export function abbreviateNumber(num, decimals = 1) {
  if (num === 0) return '0';

  // Determine the absolute value (for correct suffix calculation)
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // Suffixes for thousands, millions, billions, etc.
  const suffixes = ['', 'K', 'M', 'B', 'T'];

  // Determine the suffix index based on log base 1000
  const i = Math.floor(Math.log10(absNum) / 3);
  // The suffix is limited to the length of the suffixes array
  const suffix = suffixes[i] ?? '';

  // Scale the number by the appropriate power of 1000
  const scaled = absNum / Math.pow(1000, i);

  // Format to desired decimals and reapply negative sign if needed
  const formatted = scaled.toFixed(decimals);

  return (isNegative ? '-' : '') + formatted + suffix;
}

const BookCardSkeleton = () => (
  <div className="rounded-2xl overflow-hidden surface-card">
    <div className="skeleton aspect-[3/4]" />
    <div className="p-4 space-y-3">
      <div className="skeleton h-4 rounded-md w-4/5" />
      <div className="skeleton h-3 rounded-md w-2/5" />
      <div className="skeleton h-8 rounded-lg w-full" />
    </div>
  </div>
);

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReadings = async () => {
      const data = await fetchReadings();
      setCategories(data.categories);
      setLoading(false);
    };

    loadReadings();
  }, []);

  return (
    <div className="h-full overflow-auto">
      <div className="content-width mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--foreground)' }}>
              Library
            </h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--foreground-muted)' }}>
              Your collection, organized by category.
            </p>
          </div>
          <Link
            to="/upload"
            className="button-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm shadow-sm"
          >
            <HiOutlineUpload className="h-4 w-4" aria-hidden="true" />
            Upload New Book
          </Link>
        </header>

        {loading ? (
          <div className="space-y-10">
            {[0, 1].map((section) => (
              <section key={section} className="space-y-5">
                <div className="skeleton h-6 w-40 rounded-md" />
                <div className="grid grid-cols-1 min-[600px]:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <BookCardSkeleton key={i} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="surface-card rounded-2xl px-8 py-16 flex flex-col items-center text-center gap-3">
            <HiOutlineBookOpen className="h-10 w-10" style={{ color: 'var(--foreground-muted)' }} aria-hidden="true" />
            <p className="font-medium" style={{ color: 'var(--foreground)' }}>Your library is empty</p>
            <p className="text-sm max-w-sm" style={{ color: 'var(--foreground-muted)' }}>
              Upload an EPUB to start building your collection.
            </p>
            <Link to="/upload" className="button-primary mt-2 inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium">
              <HiOutlineUpload className="h-4 w-4" aria-hidden="true" />
              Upload New Book
            </Link>
          </div>
        ) : (
          categories.map((category) => (
            <section key={category.key} className="ink-card pt-6 space-y-5">
              <h2 className="text-lg font-semibold px-1" style={{ color: 'var(--foreground)' }}>
                {category.title}
              </h2>
              <div className="grid grid-cols-1 min-[600px]:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-6">
                {category.readings.map((reading) => (
                  <article
                    key={reading.key}
                    className="group surface-card rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:-translate-y-1"
                    style={{ boxShadow: 'var(--shadow-1)' }}
                  >
                    <Link
                      to={`/reading/${reading.key}/read`}
                      className="focus:outline-none"
                    >
                      {reading.cover ? (
                        <div className="aspect-[3/4] overflow-hidden" style={{ background: 'var(--background-subtle)' }}>
                          <img
                            src={`${API_BASE_URL}${reading.cover}`}
                            alt={reading.title}
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="aspect-[3/4] flex items-center justify-center" style={{ background: 'var(--background-subtle)' }}>
                          <HiOutlineBookOpen className="h-8 w-8" style={{ color: 'var(--foreground-muted)' }} aria-hidden="true" />
                        </div>
                      )}
                    </Link>
                    <div className="p-4 flex-1 flex flex-col justify-between gap-3">
                      <Link to={`/reading/${reading.key}/read`} className="focus:outline-none">
                        <h3 className="font-medium line-clamp-2 text-base leading-snug" style={{ color: 'var(--foreground)' }}>
                          {reading.title}
                        </h3>
                      </Link>
                      <div className="space-y-3">
                        <div className="text-xs font-medium" style={{ color: 'var(--foreground-muted)' }}>
                          {abbreviateNumber(reading.words, 0)} words
                        </div>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/reading/${reading.key}/read`}
                            className="button-secondary flex-1 inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-sm font-medium"
                          >
                            Read
                          </Link>
                          {reading.has_summary && (
                            <Link
                              to={`/reading/${reading.key}/summary`}
                              className="button-primary flex-1 inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-sm font-medium"
                            >
                              Summary
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default Home;
